import React, { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * PWA install prompt.
 *
 * Stores ONLY a timestamp in localStorage (`gj_pwa_dismissed_at`) so we
 * don't re-show the banner for `DISMISS_DAYS`. No sensitive data is
 * persisted — localStorage is intentional and appropriate here:
 *   1. The value is a public, non-identifying number.
 *   2. It must persist across reloads with no server round-trip.
 *   3. It must be readable client-side to gate UI on mount.
 */
const DISMISS_KEY = "gj_pwa_dismissed_at";
const DISMISS_DAYS = 14;

const isAlreadyInstalled = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const readDismissedAt = () => {
  try {
    return parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  } catch {
    return 0;
  }
};

const writeDismissedAt = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (err) {
    console.warn("[install-prompt] localStorage write failed:", err);
  }
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const daysSince = (Date.now() - readDismissedAt()) / (1000 * 60 * 60 * 24);
    if (isAlreadyInstalled() || daysSince < DISMISS_DAYS) return undefined;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setShow(true), 3000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (err) {
      console.warn("[install-prompt] prompt failed:", err);
    }
    setShow(false);
    setDeferred(null);
    writeDismissedAt();
  }, [deferred]);

  const dismiss = useCallback(() => {
    setShow(false);
    writeDismissedAt();
  }, []);

  if (!show || !deferred) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-[60] bg-[#0A192F] text-white rounded-xl shadow-2xl border border-[#F97316]/30 p-4 gj-fade-up"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-white/60 hover:text-white"
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F97316] grid place-items-center font-heading font-extrabold text-[#0A192F] shrink-0">
          GJ
        </div>
        <div className="pr-4">
          <div className="font-heading font-bold text-base">Install Gaddi Junction</div>
          <p className="text-xs text-white/70 mt-1 leading-relaxed">
            Get a one-tap shortcut to your favourite used-car marketplace, right on your home screen.
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button
          onClick={install}
          className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold"
          data-testid="install-prompt-install"
        >
          <Download className="w-4 h-4 mr-2" /> Install App
        </Button>
        <Button
          onClick={dismiss}
          variant="outline"
          className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent"
        >
          Later
        </Button>
      </div>
    </div>
  );
}
